import {
  setEreaderAsLastLaunched,
  setCloseReadingAsLastLaunched,
  setVocabAsLastLaunched,
} from 'a/hub/actions/studentProgressActions';
import { saveAssignmentProgress, startAssignment, updateAssignmentProgress } from 'a/hub/actions/assignmentLinkActions';
import { getCurrentAssignment } from 'a/hub/selectors/assignmentLinkSelectors';
import { updateUsageTags } from 'a/actions/appSessionActions';
import GameIds from 'a/hub/data/GameIds';
import { MAIN_ASSESSMENT_IDS, ASSESSMENT_IDS } from 'a/hub/data/AssessmentIds';
import { getLastAppLaunched } from 'a/hub/selectors/studentProgressSelectors';
import { setWorkshopStepAsInProgress } from 'a/hub/actions/workshopActions';
import { GAME_MODE, PRODUCT_LECTURA, USAGE_TAGS } from 'lib/constants';
import { SEARCHLIGHT_EVENT_TYPE } from 'lib/SearchlightDispatcher';
import { SEARCHLIGHT_ACTION_TYPE } from 'a/actions/searchlightActionCreators';
import { getGameMode, getDeepLinkProgram, getIsSeason3 } from 'a/hub/selectors';

/**
 * Updates usage tags for a specific activity (eg, game, eReader, assessment) when starting onboarding or standard round
 * @param {string} activity - gameId
 * @param {Object} params - from selectedActivity reducer - related to the quest or skill where applicable (eg, questId, skillId)
 * @param {string|null} gameMode - game mode (eg, demo, deep link, quest, practice) - null for assessments
 * @param {string|null} level - level for the activity - null for assessments and eReader
 * @param {string|null} gamePart - onboarding vs standard - null for assessments and eReader
 * @param {string|null} bookTitle - title of the book, if eReader
 * @param {Function} dispatch - redux dispatch function to send updateUsageTags action
 */
const updateUsageForActivity = ({
  product,
  program,
  activity,
  params = {},
  grade = null,
  gameMode = null,
  level = null,
  gamePart = null,
  bookTitle = null,
  dispatch,
}) => {
  const { isChallengeQuest, isSideQuest, questId, skillId } = params;
  const tagsToUpdate = {
    [USAGE_TAGS.APP.prefix]: USAGE_TAGS.APP.value[product],
    [USAGE_TAGS.GRADE.prefix]: USAGE_TAGS.GRADE.value[grade] ?? null,
    [USAGE_TAGS.LOCATION.prefix]: activity,
    [USAGE_TAGS.GAME_MODE.prefix]: USAGE_TAGS.GAME_MODE.value[gameMode] ?? null,
    [USAGE_TAGS.LEVEL.prefix]: level,
    [USAGE_TAGS.GAME_PART.prefix]: gamePart,
    [USAGE_TAGS.BOOK_TITLE.prefix]: bookTitle,
    [USAGE_TAGS.SKILL_ID.prefix]: null,
  };

  // deep link mode
  if ([GAME_MODE.PRACTICE_LINK, GAME_MODE.ASSIGNMENT_LINK, GAME_MODE.TEACHER_LINK].includes(gameMode)) {
    if (program) {
      // Program values from aqua admin for deep link only have values from USAGE_TAGS.PROGRAMS
      // This is a new feature for SY '25-26
      // Retaining the else/if blocks for backward compatibility - can be removed after this SY
      tagsToUpdate[USAGE_TAGS.PROGRAM.prefix] = program;
    } else if (gameMode === GAME_MODE.ASSIGNMENT_LINK) {
      tagsToUpdate[USAGE_TAGS.PROGRAM.prefix] = product === PRODUCT_LECTURA
        ? USAGE_TAGS.PROGRAM.value.CAMINOS : USAGE_TAGS.PROGRAM.value.CKLA;
    } else {
      tagsToUpdate[USAGE_TAGS.PROGRAM.prefix] = USAGE_TAGS.PROGRAM.value[product];
    }
  }

  // quest mode
  if (gameMode === GAME_MODE.QUEST) {
    if (isSideQuest) {
      tagsToUpdate[USAGE_TAGS.QUEST_TYPE.prefix] = USAGE_TAGS.QUEST_TYPE.value.SIDE_QUEST;
    } else if (isChallengeQuest) {
      tagsToUpdate[USAGE_TAGS.QUEST_TYPE.prefix] = USAGE_TAGS.QUEST_TYPE.value.CHALLENGE_QUEST;
    } else if (questId) {
      tagsToUpdate[USAGE_TAGS.QUEST_TYPE.prefix] = `${USAGE_TAGS.QUEST_TYPE.value.QUEST} ${questId}`;
    }

    if (skillId) {
      tagsToUpdate[USAGE_TAGS.SKILL_ID.prefix] = skillId;
    }
  }

  dispatch(updateUsageTags(tagsToUpdate));
};

const ProgressTrackerMiddleware = ({ dispatch, getState }) => next => (action) => {
  const { type, payload = { application: {} } } = action;

  // from this point, if not a searchlight dispatch event, move along immediately
  if (![
    SEARCHLIGHT_ACTION_TYPE.DISPATCH_EVENT,
    SEARCHLIGHT_ACTION_TYPE.SEARCHLIGHT_DISPATCH_EVENT,
  ].includes(type)) {
    return next(action);
  }
  // it is a searchlight event, get the searchlight event type
  const { application: { eventType, gameId, details } } = payload;
  const isAssessmentOrSubmeasure = ASSESSMENT_IDS.has(gameId);

  switch (eventType) {
    case SEARCHLIGHT_EVENT_TYPE.REPEAT_ONBOARDING: {
      // repeat onboarding gets served from the '?' button in the progress screen for any game
      // not applicable to assessments or eReader
      const state = getState();
      const {
        appSessionReducer: { product },
        persistStateReducer: { grade },
        selectedActivity: { activity, params },
      } = getState();
      const program = getDeepLinkProgram(state);
      const gameMode = getGameMode(state);
      const gamePart = USAGE_TAGS.GAME_PART.value.ONBOARDING;

      updateUsageForActivity({ product, program, grade, activity, params, gameMode, gamePart, dispatch });
      break;
    }

    case SEARCHLIGHT_EVENT_TYPE.ONBOARDING_START: {
      // due to assessments having submeasures, don't update usage tags for onboarding (over-tagging/not accurate)
      // not applicable to eReader, as it does not have onboarding
      if (!isAssessmentOrSubmeasure) {
        const state = getState();
        const {
          appSessionReducer: { product },
          persistStateReducer: { grade },
          selectedActivity: { activity, params },
        } = state;
        const program = getDeepLinkProgram(state);
        const gameMode = getGameMode(state);
        const gamePart = USAGE_TAGS.GAME_PART.value.ONBOARDING;
        updateUsageForActivity({ product, program, grade, activity, params, gameMode, gamePart, dispatch });
      }

      if (getIsSeason3(getState()) && details.isQuestWorkflow) {
        dispatch(setWorkshopStepAsInProgress(false));
      }
      break;
    }

    case SEARCHLIGHT_EVENT_TYPE.ROUND_START: {
      const state = getState();
      const {
        appSessionReducer: { product },
        eReader: { bookInfo },
        persistStateReducer: { grade },
        selectedActivity: { params },
      } = state;
      const { startDate } = getCurrentAssignment(state);
      const isEReader = gameId.includes(GameIds.E_READER);
      // Usage //
      // activity from selectedActivity reducer does not update for main assessment submeasures - use gameId instead
      const activity = product === PRODUCT_LECTURA ? gameId.replace(/_es$/, '') : gameId;
      const program = getDeepLinkProgram(state);
      // assessments are separate from game mode (eg, demo, deep link, quest, practice)
      const gameMode = !isAssessmentOrSubmeasure ? getGameMode(state) : null;
      // not applicable to assessments or eReader
      const level = !isAssessmentOrSubmeasure && !isEReader && details?.level ? details.level : null;
      // distinguish onboarding vs standard round (over-tagging/not accurate for assessments; not applicable for eReader)
      const gamePart = !isAssessmentOrSubmeasure && !isEReader ? USAGE_TAGS.GAME_PART.value.STANDARD : null;
      const bookTitle = isEReader ? bookInfo.title : null;

      // update usage tags when starting a new round
      // main assessments have round_start events, but we only want to call Usage service for individual submeasure(s)
      if (!MAIN_ASSESSMENT_IDS.has(gameId)) {
        updateUsageForActivity({ product, program, grade, activity, params, gameMode, level, gamePart, bookTitle, dispatch });
      }

      if (getIsSeason3(getState()) && details.isQuestWorkflow) {
        dispatch(setWorkshopStepAsInProgress(false));
      }

      if (gameMode === GAME_MODE.ASSIGNMENT_LINK && !startDate) {
        dispatch(startAssignment());
      }
      break;
    }

    case SEARCHLIGHT_EVENT_TYPE.LEVEL_COMPLETE: {
      const gameMode = getGameMode(getState());
      if (gameMode === GAME_MODE.ASSIGNMENT_LINK) {
        dispatch(updateAssignmentProgress({ gameId, ...details }));
        dispatch(saveAssignmentProgress());
      }
      break;
    }

    case SEARCHLIGHT_EVENT_TYPE.APP_LAUNCHED: {
      switch (gameId) {
        case GameIds.E_READER:
          dispatch(setEreaderAsLastLaunched());
          break;
        case GameIds.CLOSE_READING:
          dispatch(setCloseReadingAsLastLaunched());
          break;
        case GameIds.VOCAB: {
          dispatch(setVocabAsLastLaunched());
          break;
        }
        default:
      }
      const state = getState();
      const lastAppLaunched = getLastAppLaunched(state);
      sessionStorage.setItem('sessionProgress', JSON.stringify({ lastAppLaunched }));
      break;
    }
    default:
      break;
  }

  // we're done here, move along
  return next(action);
};

export default ProgressTrackerMiddleware;
